import { useCallback, useEffect, useState } from "react";
import { api } from "@/services/api";
import { uploadFile, formatBytes } from "@/services/upload";

interface Media {
  id: number;
  name: string;
  file_url: string;
  mime_type: string;
  size_bytes: string;
  alt_text: string;
  uploaded_at: string;
}
interface ListResponse { data: Media[]; total: number; page: number; limit: number }

type TypeFilter = "all" | "image" | "pdf" | "document" | "video";

function typeOf(mime?: string): Exclude<TypeFilter, "all"> {
  if (!mime) return "document";
  const m = mime.toLowerCase();
  if (m.startsWith("image/")) return "image";
  if (m === "application/pdf") return "pdf";
  if (m.startsWith("video/")) return "video";
  return "document";
}

const TYPE_META: Record<Exclude<TypeFilter, "all">, { label: string; icon: string; badge: string }> = {
  image: { label: "Image", icon: "ri-image-line", badge: "bg-primary-100 text-primary-800" },
  pdf: { label: "PDF", icon: "ri-file-pdf-2-line", badge: "bg-accent-100 text-accent-900" },
  document: { label: "Document", icon: "ri-file-text-line", badge: "bg-secondary-100 text-secondary-900" },
  video: { label: "Video", icon: "ri-video-line", badge: "bg-foreground-100 text-foreground-800" },
};

function folderOf(url: string): "downloads" | "uploads" {
  return url.includes("/downloads/") ? "downloads" : "uploads";
}

function formatDate(value?: string): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export default function AdminMedia() {
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [type, setType] = useState<TypeFilter>("all");
  const [addOpen, setAddOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set("search", debouncedSearch);
      params.set("page", String(page));
      params.set("limit", "24");
      const res = await api.get<ListResponse>(`/media?${params.toString()}`);
      setData(res);
    } catch (e: any) {
      setError(e?.message || "Failed to load media");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page]);

  useEffect(() => { load(); }, [load]);

  const visible = (data?.data || []).filter(
    (m) => type === "all" || typeOf(m.mime_type) === type
  );

  function toggleSelect(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected((prev) => {
      const next = new Set(prev);
      const all = visible.map((m) => m.id);
      const everySelected = all.every((id) => next.has(id));
      all.forEach((id) => (everySelected ? next.delete(id) : next.add(id)));
      return next;
    });
  }

  async function remove(id: number) {
    if (!window.confirm("Delete this media item? The file is also removed.")) return;
    try {
      await api.del(`/media/${id}`);
      setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
      load();
    } catch (e: any) {
      alert(e?.message || "Delete failed");
    }
  }

  async function bulkRemove() {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} selected item(s)?`)) return;
    setBusy(true);
    try {
      for (const id of Array.from(selected)) {
        await api.del(`/media/${id}`);
      }
      setSelected(new Set());
      setSelectMode(false);
      load();
    } catch (e: any) {
      alert(e?.message || "Delete failed");
    } finally {
      setBusy(false);
    }
  }

  async function saveEdit(id: number, patch: { name?: string; alt_text?: string }) {
    try {
      await api.put(`/media/${id}`, patch);
      setData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          data: prev.data.map((m) =>
            m.id === id
              ? { ...m, name: patch.name ?? m.name, alt_text: patch.alt_text ?? m.alt_text }
              : m
          ),
        };
      });
    } catch (e: any) {
      alert(e?.message || "Save failed");
    }
  }

  const pageCount = data ? Math.max(Math.ceil(data.total / data.limit), 1) : 1;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
        <div className="flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-2 w-full md:w-72">
          <i className="ri-search-line text-foreground-500"></i>
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search media..."
            className="flex-1 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 focus:outline-none"
          />
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <button
              onClick={bulkRemove}
              disabled={busy}
              className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-accent-600 text-background-50 px-4 py-2.5 hover:bg-accent-700 disabled:opacity-60"
            >
              <i className={busy ? "ri-loader-4-line animate-spin" : "ri-delete-bin-line"}></i>
              Delete ({selected.size})
            </button>
          )}
          <button
            onClick={() => setSelectMode((v) => { if (v) setSelected(new Set()); return !v; })}
            className={`inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full border px-4 py-2.5 transition-colors ${
              selectMode
                ? "border-primary-600 bg-primary-600 text-background-50"
                : "border-background-300 text-foreground-800 hover:bg-background-100"
            }`}
          >
            <i className="ri-checkbox-multiple-line"></i>
            {selectMode ? "Done" : "Select"}
          </button>
          <button
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-5 py-2.5 hover:bg-primary-700"
          >
            <i className="ri-upload-2-line"></i> Add Media
          </button>
        </div>
      </div>

      {/* Type filter */}
      <div className="flex flex-wrap items-center gap-2">
        {(
          [
            { key: "all", label: "All", icon: "ri-layout-grid-line" },
            { key: "image", label: "Images", icon: "ri-image-line" },
            { key: "pdf", label: "PDFs", icon: "ri-file-pdf-2-line" },
            { key: "document", label: "Documents", icon: "ri-file-text-line" },
            { key: "video", label: "Video", icon: "ri-video-line" },
          ] as { key: TypeFilter; label: string; icon: string }[]
        ).map((t) => (
          <button
            key={t.key}
            onClick={() => setType(t.key)}
            className={`inline-flex items-center gap-1.5 text-sm font-medium whitespace-nowrap rounded-full px-4 py-2 transition-colors ${
              type === t.key
                ? "bg-primary-600 text-background-50"
                : "bg-background-100 text-foreground-700 hover:bg-background-200"
            }`}
          >
            <i className={t.icon}></i>
            {t.label}
          </button>
        ))}
        <span className="ml-auto text-xs text-foreground-600">
          {visible.length} shown · {data?.total ?? 0} total
        </span>
      </div>

      {error && <div className="rounded-2xl bg-accent-100 border border-accent-300 p-4 text-sm text-accent-900">{error}</div>}

      {selectMode && (
        <div className="flex items-center gap-3 rounded-2xl bg-primary-50 border border-primary-200 px-4 py-3">
          <button onClick={toggleSelectAll} className="inline-flex items-center gap-2 text-sm font-semibold text-primary-800">
            <i className="ri-checkbox-multiple-line"></i> Select all {visible.length}
          </button>
          <span className="text-xs text-foreground-600">{selected.size} selected</span>
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {loading && (
          <p className="col-span-full py-16 text-center text-foreground-600">
            <i className="ri-loader-4-line animate-spin"></i> Loading...
          </p>
        )}
        {!loading &&
          visible.map((m) => (
            <MediaTile
              key={m.id}
              media={m}
              selectMode={selectMode}
              selected={selected.has(m.id)}
              onToggleSelect={() => toggleSelect(m.id)}
              onDelete={() => remove(m.id)}
              onSave={saveEdit}
            />
          ))}
        {!loading && visible.length === 0 && (
          <p className="col-span-full py-16 text-center text-foreground-600">
            <i className="ri-inbox-line text-3xl text-foreground-400"></i>
            <span className="block mt-2">No media found.</span>
          </p>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-end gap-1">
        <button
          onClick={() => setPage((p) => Math.max(p - 1, 1))}
          disabled={page <= 1}
          className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100 disabled:opacity-40"
        >
          <i className="ri-arrow-left-s-line"></i>
        </button>
        <span className="px-3 text-sm text-foreground-700">{page} / {pageCount}</span>
        <button
          onClick={() => setPage((p) => Math.min(p + 1, pageCount))}
          disabled={page >= pageCount}
          className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100 disabled:opacity-40"
        >
          <i className="ri-arrow-right-s-line"></i>
        </button>
      </div>

      {addOpen && (
        <AddMedia
          onClose={() => setAddOpen(false)}
          onAdded={() => { setAddOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function MediaTile({
  media,
  selectMode,
  selected,
  onToggleSelect,
  onDelete,
  onSave,
}: {
  media: Media;
  selectMode: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  onDelete: () => void;
  onSave: (id: number, patch: { name?: string; alt_text?: string }) => void;
}) {
  const isImage = media.mime_type?.startsWith("image/");
  const meta = TYPE_META[typeOf(media.mime_type)];
  const folder = folderOf(media.file_url);
  const [dims, setDims] = useState<string>("");

  return (
    <div
      className={`group relative rounded-2xl bg-background-50 border overflow-hidden transition-all ${
        selected ? "border-primary-500 ring-2 ring-primary-300" : "border-background-200 hover:border-background-300"
      }`}
    >
      {selectMode && (
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelect}
          className="absolute top-2.5 left-2.5 z-10 w-4 h-4 accent-primary-600"
        />
      )}

      {/* Preview */}
      <div className="aspect-[4/3] w-full flex items-center justify-center bg-background-100">
        {isImage ? (
          <img
            src={media.file_url}
            alt={media.alt_text || media.name}
            onLoad={(e) => {
              const img = e.currentTarget;
              if (img.naturalWidth) setDims(`${img.naturalWidth}×${img.naturalHeight}`);
            }}
            className="w-full h-full object-cover"
          />
        ) : (
          <i className={`${meta.icon} text-4xl text-foreground-400`}></i>
        )}
        <span
          className={`absolute top-2.5 right-2.5 z-10 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${meta.badge}`}
        >
          <i className={meta.icon}></i> {meta.label}
        </span>
        <span
          className={`absolute bottom-2.5 right-2.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
            folder === "downloads" ? "bg-accent-500 text-background-50" : "bg-foreground-900/70 text-background-50"
          }`}
        >
          <i className={folder === "downloads" ? "ri-download-2-line" : "ri-image-line"}></i>
          {folder}
        </span>
      </div>

      {/* Details */}
      <div className="p-3 space-y-1.5">
        <InlineText
          value={media.name}
          placeholder="File name"
          onSave={(v) => onSave(media.id, { name: v })}
        />
        <InlineText
          value={media.alt_text || ""}
          placeholder="Add alt text"
          dim
          onSave={(v) => onSave(media.id, { alt_text: v })}
        />
        <div className="flex items-center justify-between text-[11px] text-foreground-600 pt-1">
          <span>{dims || formatBytes(Number(media.size_bytes || 0))}</span>
          <span>{formatDate(media.uploaded_at)}</span>
        </div>
        {!selectMode && (
          <button
            onClick={onDelete}
            className="flex items-center gap-1 text-[11px] font-medium text-accent-700 hover:text-accent-800"
          >
            <i className="ri-delete-bin-line"></i> Delete
          </button>
        )}
      </div>
    </div>
  );
}

/** Click-to-edit text: click the label to turn it into an input; save on Enter/blur. */
function InlineText({
  value,
  placeholder,
  dim,
  onSave,
}: {
  value: string;
  placeholder: string;
  dim?: boolean;
  onSave: (v: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => { setDraft(value); }, [value]);

  if (editing) {
    return (
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { setEditing(false); const t = draft.trim(); if (t !== value) onSave(t); }}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.key === "Escape") { setDraft(value); setEditing(false); }
        }}
        className="w-full rounded-md border border-primary-400 bg-background-50 px-2 py-1 text-sm font-medium text-foreground-950 focus:outline-none focus:ring-1 focus:ring-primary-400"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => { setDraft(value); setEditing(true); }}
      title="Click to edit"
      className={`group/ed flex w-full items-center gap-1 text-left text-sm ${
        dim ? "text-foreground-600" : "font-medium text-foreground-950"
      }`}
    >
      <span className="flex-1 truncate">{value || placeholder}</span>
      <i className="ri-pencil-line text-xs text-foreground-400 opacity-0 group-hover/ed:opacity-100"></i>
    </button>
  );
}

function AddMedia({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [form, setForm] = useState({ name: "", file_url: "", mime_type: "image/jpeg", size_bytes: 0, alt_text: "" });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isPdf = form.mime_type === "application/pdf" || /\.pdf$/i.test(form.file_url.split("?")[0]);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const up = await uploadFile(file);
      setForm((f) => ({
        ...f,
        name: f.name || file.name.replace(/\.[^.]+$/, ""),
        file_url: up.url,
        mime_type: up.mime_type || file.type,
        size_bytes: up.size_bytes || file.size,
      }));
    } catch (err: any) {
      setError(err?.message || "Upload failed");
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await api.post("/media", {
        name: form.name,
        file_url: form.file_url,
        mime_type: form.mime_type,
        size_bytes: form.size_bytes || null,
        alt_text: form.alt_text,
      });
      onAdded();
    } catch (err: any) {
      setError(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-3xl bg-background-50 border border-background-200 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-100">
          <h3 className="font-heading text-lg text-foreground-950">Add Media</h3>
          <button onClick={onClose} className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100" aria-label="Close"><i className="ri-close-line"></i></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {error && <div className="rounded-lg bg-accent-100 border border-accent-300 px-4 py-3 text-sm text-accent-900">{error}</div>}

          <div className="rounded-2xl border-2 border-dashed border-background-300 p-5 text-center">
            <input
              type="file"
              id="media-file"
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.mp4,.webm"
              onChange={onFile}
              className="hidden"
            />
            <label htmlFor="media-file" className="cursor-pointer flex flex-col items-center gap-2">
              <span className="flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 text-primary-700"><i className={uploading ? "ri-loader-4-line animate-spin" : "ri-upload-cloud-2-line"}></i></span>
              <span className="text-sm font-semibold text-foreground-900">{uploading ? "Uploading..." : "Click to upload a file"}</span>
              <span className="text-xs text-foreground-600">Images &amp; media go to /uploads · PDFs go to /downloads</span>
            </label>
            {form.file_url && !uploading && (
              <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-secondary-100 text-secondary-900 px-3 py-1 text-xs font-medium">
                <i className={isPdf ? "ri-download-2-line" : "ri-check-line"}></i> {form.file_url}
              </p>
            )}
          </div>

          <div className="text-center text-xs text-foreground-500">or paste a URL below</div>

          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">File URL *</label>
            <input required value={form.file_url} onChange={(e) => setForm((f) => ({ ...f, file_url: e.target.value }))} placeholder="/uploads/... or /downloads/..." className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Name *</label>
            <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Alt Text</label>
            <input value={form.alt_text} onChange={(e) => setForm((f) => ({ ...f, alt_text: e.target.value }))} className="w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400" />
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full border border-background-300 text-foreground-800 px-5 py-2.5 hover:bg-background-100">Cancel</button>
            <button type="submit" disabled={saving || uploading} className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-5 py-2.5 hover:bg-primary-700 disabled:opacity-60">
              {saving ? <><i className="ri-loader-4-line animate-spin"></i> Saving...</> : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}